import { useState, useCallback } from 'react';
import type { PrimaryAtmosphere } from '@wineryxplorer/shared';
import { ValidationError } from '../ui/ValidationError';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface PrimaryAtmosphereSelectorProps {
  value?: PrimaryAtmosphere;
  onChange: (value?: PrimaryAtmosphere) => void;
  error?: string | string[];
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
}

const PRIMARY_ATMOSPHERE_LABELS = {
  casual: 'Casual - Relaxed, informal setting',
  upscale: 'Upscale - Sophisticated, refined atmosphere',
  rustic: 'Rustic - Country, farmhouse, traditional feel',
  modern: 'Modern - Contemporary, sleek design',
} as const;

const VALID_ATMOSPHERES: readonly string[] = ['casual', 'upscale', 'rustic', 'modern'];

export const PrimaryAtmosphereSelector = ({ 
  value, 
  onChange, 
  error, 
  disabled = false, 
  loading = false,
  required = false 
}: PrimaryAtmosphereSelectorProps) => {
  const [localError, setLocalError] = useState<string>('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setLocalError(''); // Clear local validation error
    
    if (!newValue) {
      onChange(undefined);
      return;
    }

    // Client-side validation
    if (!VALID_ATMOSPHERES.includes(newValue)) {
      setLocalError(`Primary atmosphere must be one of: ${VALID_ATMOSPHERES.join(', ')}`);
      return;
    }

    onChange(newValue as PrimaryAtmosphere);
  }, [onChange]);

  const displayError = error || localError;
  const hasError = Boolean(displayError);

  return (
    <div className="space-y-3">
      <label 
        htmlFor="primaryAtmosphere" 
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        Primary Atmosphere
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <select
          id="primaryAtmosphere"
          value={value || ''}
          onChange={handleChange}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 bg-gray-50 touch-manipulation transition-colors ${
            hasError
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-info-500 focus:border-info-500'
          } ${
            disabled || loading
              ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
              : 'hover:border-gray-400'
          }`}
          style={{ minHeight: '44px' }} // Ensure minimum touch target size
          aria-invalid={hasError}
          aria-describedby={hasError ? 'primaryAtmosphere-error' : undefined}
        >
          <option value="">Select primary atmosphere...</option>
          {Object.entries(PRIMARY_ATMOSPHERE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <LoadingSpinner size="sm" className="text-gray-400" />
          </div>
        )}
      </div>
      
      <ValidationError 
        error={displayError} 
        className="mt-1"
      />
    </div>
  );
};